import { z } from 'zod'
import { boolean, dateTime, enumerated, object, string } from '~/prix'
import type { UserManagementRole } from './userManagementPermissions'

export type UserType = {
  id: string
  name: string
  email: string
  role: string
  password: string
  isEmailVerified: boolean
  cpf: string
  clientId: string
  createdAt: Date | null
  updatedAt: Date | null
  deletedAt: Date | null
}

export function isValidUserType(value: unknown, basicValidation?: boolean): value is UserType {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  if (typeof (value as UserType).id !== 'string') {
    return false
  }
  if (typeof (value as UserType).name !== 'string') {
    return false
  }
  if (typeof (value as UserType).email !== 'string') {
    return false
  }
  if (typeof (value as UserType).role !== 'string') {
    return false
  }
  if (typeof (value as UserType).password !== 'string') {
    return false
  }
  if (basicValidation) {
    return true
  }
  if (typeof (value as UserType).createdAt !== 'object' && (value as UserType).createdAt !== null) {
    return false
  }
  if (typeof (value as UserType).updatedAt !== 'object' && (value as UserType).updatedAt !== null) {
    return false
  }
  if (typeof (value as UserType).deletedAt !== 'object' && (value as UserType).deletedAt !== null) {
    return false
  }

  return true
}

export const userObject = object({
  id: string().isRequired(),
  name: string().isRequired(),
  email: string().isRequired(),
  cpf: string().isOptional(),
  isEmailVerified: boolean().isOptional(),
  clientId: string().isOptional(),
  createdAt: dateTime().isOptional(),
  updatedAt: dateTime().isOptional(),
  deletedAt: dateTime().isOptional(),
  role: string().isRequired(),
  firebaseData: object({
    displayName: string().isOptional(),
    photoURL: string().isOptional(),
    phoneNumber: string().isOptional(),
    lastSignInTime: string().isOptional(),
    creationTime: string().isOptional(),
    disabled: boolean(),
    emailVerified: boolean(),
  }),
})

export const userRoleEnum = [
  {
    label: 'Administrador do Sistema',
    value: 'systemAdministrator',
  },
  {
    label: 'Administrador Geral',
    value: 'generalAdministrator',
  },
  {
    label: 'Profissional Geral',
    value: 'generalProfessional',
  },
  {
    label: 'Administrador de Segurança Pública',
    value: 'publicSafetyAdministrator',
  },
  {
    label: 'Agente de Segurança Pública',
    value: 'publicSafetyAgent',
  },
  {
    label: 'Administrador de Saúde',
    value: 'healthAdministrator',
  },
  {
    label: 'Agente de Saúde',
    value: 'healthAgent',
  },
  {
    label: 'Administrador da Secretaria da Agricultura',
    value: 'agricultureAdministrator',
  },
  {
    label: 'Profissional da Secretaria da Agricultura',
    value: 'agricultureProfessional',
  },
]

export const userRoleEnumerated = (allowedRoles?: UserManagementRole[]) => {
  const rolesToUse = allowedRoles?.length
    ? userRoleEnum.filter(item => allowedRoles.includes(item.value as UserManagementRole))
    : userRoleEnum

  return {
    keysAndLabels: rolesToUse.reduce((acc, item) => {
      acc[item.value] = item.label
      return acc
    }, {} as Record<string, string>),
  }
}

const rolesEnumValues: string[] = userRoleEnum.map(item => item.value)

const baseSchemaUser = z.object({
  name: z.string().min(3, 'Nome deve conter no mínimo 3 caracteres'),
  email: z.string().email('E-mail inválido'),
  role: z.enum([rolesEnumValues[0], ...rolesEnumValues.slice(1)] as [string, ...string[]], {
    message: 'Selecione uma das funções',
  }),
})

const passwordSchema = z.string().min(6, 'A senha deve ter pelo menos 6 caracteres')
const emptyStringToUndefined = (value: unknown) => (value === '' ? undefined : value)

export const schemaUserCreate = baseSchemaUser
  .extend({
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Confirme a senha'),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'As senhas estão diferentes',
    path: ['confirmPassword'],
  })

export const schemaUserUpdate = baseSchemaUser
  .extend({
    password: z.preprocess(emptyStringToUndefined, passwordSchema.optional()),
    confirmPassword: z.preprocess(emptyStringToUndefined, z.string().optional()),
  })
  .refine(data => !data.password || data.password === data.confirmPassword, {
    message: 'As senhas estão diferentes',
    path: ['confirmPassword'],
  })

export const schemaUser = schemaUserCreate
